import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { QueryStates } from '../../../chrome/state';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import type { Extreme, Localities } from '../api/schemas';
import { tt } from './messages';
import { compact, type RailMetric } from './NeighbourhoodsCard';

const MINUS = '−';

function signedCompact(delta: number): string {
  const text = compact(Math.abs(delta), getLocale());
  return delta > 0 ? `+${text}` : delta < 0 ? `${MINUS}${text}` : text;
}

function ExtremeRow(props: { label: string; extreme: Extreme | null; none: string }): ReactNode {
  const { extreme } = props;
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-col">
        <span className="text-body">{props.label}</span>
        <span className="text-caption text-muted-foreground">
          {extreme === null ? props.none : extreme.name}
        </span>
      </div>
      {extreme !== null && (
        <span className="text-title-sm" data-numeric>
          {signedCompact(extreme.delta)}
        </span>
      )}
    </div>
  );
}

/** The largest rise and fall the route computed for the rail's figure; the
 *  view never picks its own. */
export function MovementCard(props: {
  query: UseQueryResult<Localities, ApiFailureError>;
  metric: RailMetric;
}): ReactNode {
  return (
    <Card className="gap-0">
      <CardHeader>
        <h2 className="text-title-sm">{tt('movement.title')}</h2>
      </CardHeader>
      <CardContent>
        <QueryStates query={props.query} isEmpty={() => false} empty={null}>
          {(data) => {
            const extremes =
              props.metric === 'comments' ? data.extremes.comment_volume : data.extremes.reach;
            return (
              <div className="flex flex-col gap-3">
                <ExtremeRow
                  label={tt('movement.rise')}
                  extreme={extremes.largest_rise}
                  none={tt('movement.rise.none')}
                />
                <ExtremeRow
                  label={tt('movement.fall')}
                  extreme={extremes.largest_fall}
                  none={tt('movement.fall.none')}
                />
              </div>
            );
          }}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
